// Types only: settings must not drag the SDK (nor React components) in — they
// are read by plain helpers and by tests that do not mount anything.
import type { MeetingSource } from "@kalyzee/kast-web-sdk";
import { OvenMediaRetransmissionParams } from "@kalyzee/kast-web-sdk";
import type { MeetingPrivacy } from "@kalyzee/kast-web-sdk/http";
import type { AudioMixerOptions } from "@kalyzee/kast-web-sdk/web-components";
import deepEqual from "deep-equal";
import objectAssignDeep from "object-assign-deep";
import type { MeetingSourceExtended } from "../hooks/meeting";
import { validateCompositionSettings } from "./composition";
import { validateEmissionSettings } from "./emission";
import { getEnvSettings, initEnv } from "./env";
import { appMessage } from "./message";
// Deep import (not the barrel): settings must not drag React components in.
import { Composition } from "../interfaces/composition";
import { SecondaryScreenLayout } from "../interfaces/secondaryScreens";
import { GridMode } from "../interfaces/ui";
import { getGlobalQuery, NavigateGlobalQuery } from "./navigation/queries";
import {
  getSelectedSettingsKey,
  getSettings,
  getSettingsByKey,
  getSettingsSources,
  listSettingsKeys,
  removeSettingsByKey,
  setSelectedSettingsKey,
  setSettingsByKey,
  setSettingsSources,
  SETTINGS_DEFAULT_KEY,
  SETTINGS_KEY_PREFIX,
  SETTINGS_LEGACY_KEY,
  StorageSettingsSource,
  StorageSettingsVariable,
} from "./storage";
// Circular at module level (settingsSchema imports decodeBase64Url from here),
// but both sides only reference each other at call time — safe under ESM.
import { fetchSettingsSource, restoreSettingsFromForm, sanitizeSettings, settingsSchema } from "./settingsSchema";

export interface SettingsSecret {
  adminPin?: string;
  localPassword?: string | null;
  remotePassword?: string | null;
}

export interface SettingsDebug {
  displaySessionIds?: boolean;
  displayIncomingStreamStats?: boolean;
  displayOutgoingStreamStats?: boolean;
  audio?: {
    mixer?: AudioMixerOptions & { public?: boolean };
  };
  sdkClient?: {
    public?: boolean;
  };
  meetingContext?: {
    public?: boolean;
  };
}

export interface SettingsMessagingBridgeMaster {
  enableBroadcast?: boolean;
}

export interface SettingsMessagingBridgeSlave {
  enableBroadcast?: boolean;
  enableWebsocket?: boolean;
  websocketEndpoint?: string;
}

/**
 * Where the WebCodecs replay engine may be picked: `'apple'` on Apple devices, Mac included
 * (the default), `'true'` wherever it can run, `'false'` nowhere. The booleans the setting held
 * before it grew its third mode are still read as `'true'` / `'false'`.
 */
export type VodWebCodecsMode = "true" | "false" | "apple";

export interface SettingsVod {
  /**
   * Sequential tile mounting in the replay player: at most one tile creates its media player at
   * a time, the next being admitted once the previous confirmed its init (metadata decoded).
   * Damps hardware-decoder init/teardown collisions on fragile platforms (VAAPI Linux/Intel).
   * Default: off (all tiles mount in the same tick, historical behavior).
   */
  sequentialTileMounts?: boolean;
  /**
   * Playback engines MultiAVPlayer may pick for the replay (see resolveVodEngines in the VOD
   * player). Capability always wins: an engine the browser cannot run is never picked, so
   * disabling mediaElement only avoids it where WebCodecs can actually run. A config with
   * neither engine falls back to mediaElement.
   */
  engines?: {
    /** Unset = 'apple'. Strings because the settings form's select has no boolean values. */
    webcodecs?: VodWebCodecsMode;
    /** Unset = on. */
    mediaElement?: boolean;
    /**
     * WebCodecs engine only. Unset/false = video decoders are leased from a page-global pool and
     * reused across seeks, restarts and clip transitions instead of being recreated (a platform
     * decoder session each time — the churn behind wedged AppleGVA accelerators on macOS).
     * True = the SDK's decoder-per-iterator behavior, for comparisons.
     */
    disableWebcodecsDecoderPool?: boolean;
  };
}

export interface SettingsLogs {
  main?: boolean;
  /** Passive camera/mic diagnostics (see helpers/mediaDiagnostics.ts). */
  mediaDiagnostics?: boolean;
  meetingContext?: boolean;
  /** VOD replay player diagnostics (MultipleVideoPlayer / Video internals). */
  vodPlayer?: boolean;
  ovenMediaSession?: {
    reception?: {
      main?: boolean;
      webrtc?: boolean;
    };
    emission?: {
      main?: boolean;
      webrtc?: boolean;
    };
    retransmission?: {
      main?: boolean;
      webrtc?: boolean;
    };
  };
}

/** OvenMedia emitter options, as accepted by the SDK retransmission params. */
export type SettingsRetransmissionSessionOptions = NonNullable<OvenMediaRetransmissionParams["sessionOptions"]>;

export interface SettingsRetransmissionDestination {
  /**
   * Stable identifier of the destination among the retransmissions of a same
   * source (used as the SDK destination id: changing it restarts the publish).
   */
  id: string;
  /**
   * matchEngine expression deciding which meeting streams feed this destination.
   * Applied to a context composed with:
   * - type ('emission' = my published medias | 'reception' = received streams | 'custom')
   * - key (custom sources only, e.g. 'audio-mixer')
   * - session, media (resolved fresh from the meeting store on every evaluation)
   * - currentSession (my session), meeting
   * - highlighted (session.highlighting = ongoing)
   */
  match: string;
  /**
   * Destination URL (OvenMedia signaling), query params included — they survive
   * the emitter's own URL processing (e.g. "...?record=true"). `{{dotted.path}}`
   * placeholders are substituted (templateEngine) from the exact same context as
   * `match`, e.g. {{media.mediaDestination}} (the source's own publish URL, self
   * medias only), {{media.mediaSrc}}, {{meeting.id}}, {{session.id}},
   * {{media.id}}, {{session.user.id}}, {{key}}, {{nonce}}, {{attempt}}. {{nonce}}
   * and {{attempt}} are resolved at every publish attempt (recreations and retries):
   * {{nonce}} is a fresh random key, so a republished stream never reuses a name the
   * server may not have released yet (e.g. ".../retrans-{{session.id}}-{{nonce}}");
   * {{attempt}} is the retry counter (0 = first attempt). A placeholder may carry a
   * default for the keys that are not always there: {{meeting.record,false}}. The
   * publish fails and is retried while a placeholder without default cannot be
   * resolved.
   */
  url: string;
  /**
   * Optional template (same placeholders as `url`) telling when a live retransmission
   * must be restarted towards a new target: the publish is torn down and recreated as
   * soon as this resolves to a different value. Needed because the url is resolved per
   * publish attempt, hence not comparable by itself — put in here whatever part of the
   * url identifies the target, e.g. "{{media.mediaDestination}}". Left out (or
   * unresolvable): the retransmission is never restarted on a context change.
   */
  srcKey?: string;
  /** Which tracks feed this destination (default: both). */
  audio?: boolean;
  video?: boolean;
  /**
   * Publish clones of the source tracks (default: true), so the retransmission survives the
   * app disabling the originals (paused reception, local mutes). Set to false to publish the
   * source tracks themselves — the retransmission then follows their mutes. Read when the
   * publish is (re)created or its stream replaced: a change alone does not restart it.
   */
  cloneTracks?: boolean;
  /**
   * Complete the published stream with a silent audio / black video track when the source has
   * no track of that kind (default: false). Our OvenMedia refuses single-kind publishes (403):
   * enable the missing kind for an audio-only (mixer tap) or video-only destination. At least
   * one REAL track among the selected kinds is still required — an empty track never replaces
   * an absent source.
   */
  fillMissingAudioTrack?: boolean;
  fillMissingVideoTrack?: boolean;
  /**
   * Publish towards the destination BEFORE the source stream exists (default: false).
   * Receptions gain the most: the destination connection is negotiated from the media
   * session's announcement, in parallel with the source's own negotiation, on silent/black
   * filler tracks; the real tracks are swapped in without renegotiation when the stream
   * arrives. Cuts several seconds off the head of records — the destination receives a
   * short black/silent lead-in instead of missing the first real seconds. Pair with
   * fillMissing(Audio|Video)Track when the source may permanently lack a selected kind.
   */
  earlyStart?: boolean;
  /**
   * Republish the received frames as-is, without decoding then re-encoding them (default:
   * false) — same quality as the source, far less CPU. Receptions only (a `type = 'reception'`
   * match): the receiver must be created with the matching encoded-frame interceptor, which
   * the SDK enables from this very flag, so a destination that only starts matching once the
   * reception is live silently falls back to the decoded tracks. Ignored on the kind the
   * destination does not republish (`audio` / `video` false).
   */
  bypassAudio?: boolean;
  bypassVideo?: boolean;
  /**
   * OvenMedia emitter options of this publish (bitrates, transports, audio.mono/dtx,
   * video.content...), shallow-merged (per top-level key) over the retransmission manager
   * ones. Read at publish creation only: a change applies to the next publish, so pair it
   * with an `id`/`srcKey` change to force a restart.
   */
  sessionOptions?: SettingsRetransmissionSessionOptions;
  /** Grace period (ms) before stopping when the match stops applying (default 0). */
  stopDelay?: number;
}

/** Who may speak when a participant joins an instant meeting. */
export type InstantMeetingSpeakingMode = "conference" | "free";

export interface Settings {
  secret?: SettingsSecret;
  debug?: SettingsDebug;
  roomId?: string;
  bridge?: {
    master?: SettingsMessagingBridgeMaster;
    slave?: SettingsMessagingBridgeSlave;
  };
  sources?: Partial<MeetingSourceExtended>[];
  /**
   * Source walls rendered by `/composition/:id` — a set of sources laid out on
   * a dedicated full-screen page, outside any meeting. Their own `sources` are
   * never published, unlike the ones above.
   */
  compositions?: Composition[];
  videoFeedback?: {
    source: MeetingSource;
  };
  webcamFeedback?: {
    source: Partial<MeetingSourceExtended>;
  };
  emission?: {
    sdpFormatter?: {
      offer?: string;
      answer?: string;
    };
  };
  retransmission?: {
    /**
     * Config-driven retransmissions: each destination matches meeting streams via
     * a matchEngine expression and republishes them towards an OvenMedia URL
     * (record, external restream...). Empty/absent = feature disabled.
     */
    destinations?: SettingsRetransmissionDestination[];
  };
  /**
   * Media events: what each stream this page publishes — its own medias as well as the
   * ones it republishes — reports to the recording middleware (camera cut, mic cut, picture
   * hidden, track lost...), so a replay can say why a tile is black.
   */
  mediaEvents?: {
    /**
     * Base URL of the recording middleware's media-events route
     * (`<endpoint>/<app>/<stream>`). Unset, the SDK derives it from each publish
     * URL's host (`https://<host>/middleware/events/media`); set it where the
     * publish host does not front the middleware, e.g. `http://localhost:8088/events/media`
     * on the local dev stack.
     */
    endpoint?: string;
  };
  dev?: boolean;
  logs?: SettingsLogs;
  /** Replay (VOD) player behavior. */
  vod?: SettingsVod;
  soundIndicators?: boolean;
  carousel?: {
    prelaodDelay?: number;
    interval?: number;
    layouts?: SecondaryScreenLayout[];
    filters?: {
      carouselId: string;
      /**
       * Complex match string, applied to a context composed with :
       * - session
       * - media
       * - currentSession
       */
      value: string;
    }[];
  };
  ui?: {
    hideLogoutButton?: boolean;
    /** Hide the "Fermer l'application" button even when the wrapper allows it. */
    hideCloseAppButton?: boolean;
    /**
     * Show the "Fermer l'application" button without waiting for the wrapper to
     * announce `canClose` (browser testing).
     */
    forceCloseAppButton?: boolean;
    /** Hide the "Paramètres de l'application" button even when the wrapper allows it. */
    hideOpenSettingsButton?: boolean;
    /**
     * Show the "Paramètres de l'application" button without waiting for the
     * wrapper to announce `canOpenSettings` (browser testing).
     */
    forceOpenSettingsButton?: boolean;
    showExpectedRoleBadge?: boolean;
    showFullscreenButton?: boolean;
    /**
     * Show a date selector above the events agenda on the list page, to browse
     * another day than today.
     */
    showAgendaDatePicker?: boolean;
    /**
     * LMS/iframe integration mode: /list becomes a "join a meeting" screen and
     * leaving a meeting always exits to its lobby (never back to the list).
     */
    disableMeetingList?: boolean;
    /**
     * Disable clamping of future server timestamps for elapsed counters. When a
     * client clock lags behind the server, `startedAt`/`pausedAt` can appear to
     * be in the future and counters would show 00:00:00 until the clock catches
     * up. By default the reference is clamped to "now" so counters start
     * immediately; set this to keep the legacy frozen-at-0 behavior.
     */
    disableTimeClamping?: boolean;
    /**
     * Replay player: holding the finger still on the timeline for a second zooms it around
     * that point (an overview of the whole recording appears above). Default: off — the long
     * press is easy to trigger by accident on a touch screen, and it moves the scrub target
     * under the finger already on it. Set to turn it on.
     */
    enableTimelineZoom?: boolean;
    /**
     * How the cropped ("wide") players may be moved, for every minimap. `horizontal` (default)
     * pans sideways only, no zoom; `free` adds pinch/wheel zoom, and with it vertical panning.
     */
    minimapPan?: "horizontal" | "free";
    grid?: {
      /** Grid disposition for the `Full` layout. Default: adaptive. */
      mode?: GridMode;
      /** Players per page. Leave unset (or 0) for automatic (screen-based). */
      pageSize?: number;
      /** Minimum readable tile short-side (CSS px) used in automatic mode. Default: 180. */
      autoTileMinPx?: number;
    };
  };
  /**
   * "Créer un meeting instantané" popup, on the list page. Every option of a
   * choice can be withdrawn on its own: a choice left with a single option has
   * nothing to ask, so it disappears from the popup and that option is what
   * every instant meeting is created with.
   */
  instantMeeting?: {
    /**
     * Master switch. Absent/true: the creation button (and the "Instantanés"
     * tab) show for anyone holding `meeting:create:any`. False hides both.
     */
    enabled?: boolean;
    /**
     * Privacy options offered in the popup. `public` / `private` absent = offered;
     * `closed` ("Restreint") is opt-in (absent = not offered) as it needs the
     * creator to pick who may join, which takes `user:read:any` +
     * `identity:read:any`.
     */
    privacy?: {
      /** Anyone may join. */
      public?: boolean;
      /** Members of the organization only. */
      private?: boolean;
      /** Only the users the creator picks may join — written as meeting actors. Nobody is notified. */
      closed?: boolean;
      /**
       * Option pre-selected when the choice is offered — also the value used
       * when every option is withdrawn. Default: public (or, when public is
       * withdrawn, the first option left).
       */
      default?: MeetingPrivacy;
    };
    /** Speaking-mode options offered in the popup. Each one absent/true = offered. */
    speakingMode?: {
      /** Newcomers join as spectators: only speakers and the board may talk. */
      conference?: boolean;
      /** Newcomers join as normal sessions: everyone may talk. */
      free?: boolean;
      /** Same rule as `privacy.default`. Default: conference. */
      default?: InstantMeetingSpeakingMode;
    };
    /**
     * Recording of the created meeting. Opt-in, unlike the choices above: with
     * nothing offered here the popup asks nothing and `record` is left out of
     * the creation, so the organization's own default applies.
     */
    record?: {
      /** Offer "recorded" (record: true). */
      on?: boolean;
      /** Offer "not recorded" (record: false). */
      off?: boolean;
      /** Option pre-selected when both are offered. Default: not recorded. */
      default?: boolean;
    };
  };
  features?: {
    survey?: boolean;
    board?: boolean;
    boardMatch?: string;
    audioMatch?: string;
    highlightAutoAcceptTimer?: number; // seconds before auto-accepting highlight invitation (undefined = disabled)
    rememberGuests?: boolean; // persist guest identities per meeting for later reuse (absent = disabled)
  };
}

// ---------------------------------------------------------------------------
// Settings are resolved from 3 layers, lowest to highest priority:
//   1. env    — SETTINGS env var (full Settings JSON: Docker / VITE_SETTINGS)
//   2. url    — transient `?settings=` overlay, never persisted
//   3. stored — localStorage, the only layer writes go to
// ---------------------------------------------------------------------------

interface SettingsLayers {
  env: Partial<Settings>;
  url: Partial<Settings>;
  /** Active config with its variables substituted — what the app runs on. */
  stored: Settings;
  /** The same config as written: the template, placeholders included. Merges
      and persistence go through it, so a write-back never bakes values in. */
  storedRaw: Settings;
}

let layers: SettingsLayers | undefined;
let resolved: Settings = {};
const listeners = new Set<() => void>();

/** `logs` was a plain boolean ("everything on/off") before schema v10 — normalize legacy data. */
const normalizeSettings = (settings: Partial<Settings>): Partial<Settings> => {
  const logs = settings.logs as SettingsLogs | boolean | undefined;
  if (typeof logs === "boolean") {
    settings.logs = logs
      ? {
          main: true,
          mediaDiagnostics: true,
          meetingContext: true,
          ovenMediaSession: { reception: { main: true, webrtc: true }, emission: { main: true, webrtc: true } },
        }
      : {};
  }
  // Heal form-shape leftovers (JSON kept as strings — e.g. web `children`
  // stringified by older saves or pushed back by a host): restore is a no-op
  // on data already in the persisted shape.
  return restoreSettingsFromForm(settings, settingsSchema);
};

const parseSettingsObject = (str: string): Partial<Settings> | undefined => {
  try {
    const json = JSON.parse(str);
    if (json && typeof json === "object" && !Array.isArray(json)) return normalizeSettings(json);
  } catch (err) {}
  return undefined;
};

const readEnvLayer = (): Partial<Settings> => {
  initEnv(); // idempotent — settings may be read before SdkContextProvider mounts
  const str = getEnvSettings();
  return (str && parseSettingsObject(str)) || {};
};

// ---- Multi-config: configs live under `settings-<slug>` keys; the active one
// is pointed to by `selected-settings-key` (fallback: the default config,
// stored under `settings-default` or the legacy `settings` key). ----

/** Key of the active config — falls back to the default when the pointer is absent or dangling. */
export const resolveActiveSettingsKey = (): string => {
  const selected = getSelectedSettingsKey();
  // A remote config exists as soon as its source is declared: it holds no data
  // until its first successful fetch, so the source entry counts as existence.
  if (selected && (getSettingsByKey(selected) !== null || getSettingsSources()[selected]?.mode === "remote")) return selected;
  return SETTINGS_DEFAULT_KEY;
};

const readConfigData = (key: string): Settings => {
  try {
    let str = getSettingsByKey(key);
    if (str === null && key === SETTINGS_DEFAULT_KEY) str = getSettings(); // legacy `settings` key
    if (str) return normalizeSettings(JSON.parse(str));
  } catch (err) {}
  return {};
};

const writeConfigData = (key: string, settings: Settings): void => {
  setSettingsByKey(key, JSON.stringify(settings));
  // The default config supersedes the legacy `settings` key: drop it once the
  // new key exists, otherwise deleting/emptying the default would resurrect
  // stale legacy data through the read fallback.
  if (key === SETTINGS_DEFAULT_KEY && getSettings() !== null) {
    removeSettingsByKey(SETTINGS_LEGACY_KEY);
  }
};

/**
 * Content a config actually contributes: as stored for a local one, variables
 * substituted for a remote one (whose stored data is the template as fetched).
 */
const resolveConfigData = (key: string, raw: Settings = readConfigData(key)): SettingsSubstitutionResult => {
  const variables = getSettingsConfigSource(key).variables ?? [];
  if (!variables.length && !hasSettingsPlaceholder(raw)) return { settings: raw, missing: [] };
  return substituteSettingsVariables(raw, variables);
};

const readStoredLayers = (): { stored: Settings; storedRaw: Settings } => {
  const storedRaw = readConfigData(resolveActiveSettingsKey());
  return { storedRaw, stored: resolveConfigData(resolveActiveSettingsKey(), storedRaw).settings };
};

const resolve = (l: SettingsLayers): Settings => objectAssignDeep({}, l.env, l.url, l.stored);

const ensureLayers = (): SettingsLayers => {
  if (!layers) {
    layers = { env: readEnvLayer(), url: {}, ...readStoredLayers() };
    resolved = resolve(layers);
  }
  return layers;
};

const notify = () => {
  // `resolve` deep-copies the three layers, so a write that changes nothing
  // still yields brand-new objects. Downstream that is not free: the SDK
  // rebuilds a source's media whenever the source object changes identity (and
  // drops the tracks being emitted). Keep the previous view when the content is
  // unchanged — a re-broadcast of identical settings must be a no-op.
  const next = resolve(ensureLayers());
  if (deepEqual(next, resolved)) return;
  resolved = next;
  listeners.forEach((cb) => cb());
};

/** Re-read a config from storage and, when it is the active one, apply it live. */
const applyConfigIfActive = (key: string): void => {
  if (key !== resolveActiveSettingsKey()) return;
  const next = readStoredLayers();
  Object.assign(ensureLayers(), next);
  try {
    appMessage.postSettings(next.storedRaw);
  } catch (err) {}
  notify();
};

/** 3-layer merged view (env < url < stored). Stable reference between updates. */
export const getResolvedSettings = (): Settings => {
  ensureLayers();
  return resolved;
};

/**
 * Raw localStorage layer — what the settings form and the host bridge read/write.
 * Placeholders are NOT substituted here: a host reading then writing settings
 * back must not turn the template into one machine's resolved values.
 */
export const getStoredSettings = (): Settings => ensureLayers().storedRaw;

export const subscribeSettings = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const updateStoredSettings = (partial: Partial<Settings>, options?: { merge?: boolean; persist?: boolean }): Settings => {
  const l = ensureLayers();
  // Merge against the stored layer only: merging against the resolved view
  // would bake transient env/URL values into localStorage.
  // Normalized because callers include the host bridge (localstorage_set_settings),
  // whose payload may carry form-shape leftovers (JSON as strings).
  const key = resolveActiveSettingsKey();
  const next: Settings = normalizeSettings(options?.merge ? objectAssignDeep({}, l.storedRaw, partial) : objectAssignDeep({}, partial));
  l.storedRaw = next;
  l.stored = resolveConfigData(key, next).settings;
  // A remote config owns its stored data (it is a cache of the URL): keep the
  // write in memory for this session rather than writing something the next
  // fetch would silently drop.
  if (options?.persist && getSettingsConfigSource(key).mode === "remote") {
    console.warn("[settings] Active config is remote — settings write kept in memory, not persisted");
  } else if (options?.persist) {
    try {
      writeConfigData(key, next);
      appMessage.postSettings(next);
    } catch (err) {}
  }
  notify();
  return l.stored;
};

// ---------------------------------------------------------------------------
// Config management (multi-config)
// ---------------------------------------------------------------------------

export interface SettingsConfigInfo {
  key: string;
  name: string;
  selected: boolean;
  mode: SettingsConfigMode;
  /** Remote configs only: where the content is fetched from. */
  url?: string;
}

/** "Salle Averroès !" -> "salle-averroes" */
export const slugifySettingsConfigName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip accents
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Every stored config (default first), flagged with the active one. */
export const listSettingsConfigs = (): SettingsConfigInfo[] => {
  const active = resolveActiveSettingsKey();
  const sources = getSettingsSources();
  const keys = new Set(listSettingsKeys());
  // Remote configs are cache-less until their first fetch: their source entry is
  // what makes them exist (a local entry only carries variables).
  Object.keys(sources).forEach((key) => {
    if (sources[key]?.mode === "remote") keys.add(key);
  });
  keys.add(SETTINGS_DEFAULT_KEY); // always exists, possibly only as the legacy key
  return [...keys]
    .sort((a, b) => {
      if (a === SETTINGS_DEFAULT_KEY) return -1;
      if (b === SETTINGS_DEFAULT_KEY) return 1;
      return a.localeCompare(b);
    })
    .map(
      (key) =>
        ({
          key,
          name: key.slice(SETTINGS_KEY_PREFIX.length),
          selected: key === active,
          mode: sources[key]?.mode === "remote" && sources[key]?.url ? "remote" : "local",
          url: sources[key]?.url,
        } as SettingsConfigInfo)
    );
};

/** Config content as stored — for a remote one, the template with its `[[NAME]]` placeholders. */
export const getSettingsConfigData = (key: string): Settings => readConfigData(key);

/** Config content as the app sees it: placeholders replaced by the local values. */
export const getSettingsConfigResolvedData = (key: string): SettingsSubstitutionResult => resolveConfigData(key);

/** Persist a config; if it is the active one, the app picks it up immediately. */
export const saveSettingsConfigData = (key: string, settings: Settings): void => {
  // A remote config is read-only: its stored data is only a cache of the URL.
  if (getSettingsConfigSource(key).mode === "remote") {
    throw new Error("Cette configuration est distante : son contenu provient de l'URL et ne peut pas être modifié ici");
  }
  writeConfigData(key, settings);
  if (key === resolveActiveSettingsKey()) {
    ensureLayers().stored = settings;
    try {
      appMessage.postSettings(settings);
    } catch (err) {}
    notify();
  }
};

/** Create a config from a display name (throws if invalid or already taken). */
export const createSettingsConfig = (name: string, data: Settings): SettingsConfigInfo => {
  const slug = slugifySettingsConfigName(name);
  if (!slug) throw new Error("Nom invalide : il doit contenir au moins un caractère alphanumérique");
  const key = SETTINGS_KEY_PREFIX + slug;
  if (key === SETTINGS_DEFAULT_KEY || getSettingsByKey(key) !== null) {
    throw new Error(`Une configuration « ${slug} » existe déjà`);
  }
  writeConfigData(key, data);
  return { key, name: slug, selected: false, mode: "local" };
};

/**
 * Delete a config; if it was the active one, the default takes over.
 * The default cannot be deleted (it is the resolution fallback) — empty it instead.
 */
export const deleteSettingsConfig = (key: string): void => {
  if (key === SETTINGS_DEFAULT_KEY) throw new Error("La configuration par défaut ne peut pas être supprimée");
  const wasActive = key === resolveActiveSettingsKey();
  removeSettingsByKey(key);
  setSettingsConfigSource(key, undefined);
  if (wasActive) selectSettingsConfig(SETTINGS_DEFAULT_KEY);
};

/** Make a config the active one (the app switches to it immediately). */
export const selectSettingsConfig = (key: string): void => {
  setSelectedSettingsKey(key);
  const next = readStoredLayers();
  Object.assign(ensureLayers(), next);
  try {
    appMessage.postSettings(next.storedRaw);
  } catch (err) {}
  notify();
  // The cache is what was applied above; refresh it in the background so the
  // newly active config is up to date without waiting for a reload.
  const source = getSettingsConfigSource(key);
  if (source.variablesMode === "remote") loadRemoteSettingsVariables(key);
  if (source.mode === "remote") loadRemoteSettingsConfig(key);
};

// ---------------------------------------------------------------------------
// Config source: local or remote
//
// A `local` config is what has always existed: the popup edits it and it lives
// in localStorage. A `remote` config takes its content from a URL — the config
// key then only holds a cache of the last successful fetch, so the app can boot
// synchronously (and survive an unreachable URL). The URL is checked when it is
// defined and re-fetched at every app launch; both report their errors.
// ---------------------------------------------------------------------------

export type SettingsConfigMode = "local" | "remote";

export interface SettingsConfigSource {
  mode: SettingsConfigMode;
  /** Remote configs only. */
  url?: string;
  /** Remote configs only: timestamp (ms) of the last successful fetch. */
  fetchedAt?: number;
  /** Values substituted into the `[[NAME]]` placeholders. */
  variables?: SettingsVariable[];
  /** Where those values come from (default: local). */
  variablesMode: SettingsConfigMode;
  /** Remote variables only: the URL they are fetched from. */
  variablesUrl?: string;
  /** Remote variables only: timestamp (ms) of the last successful fetch. */
  variablesFetchedAt?: number;
}

// ---- Variables: `[[NAME]]` placeholders resolved from locally stored values ----
//
// A remote config is a template: one file shared by several rooms, each machine
// holding its own values (room id, camera address, secrets that have no place on
// the server). The cache keeps the template as fetched — substitution happens on
// every read, so editing a value applies at the next launch even offline.

export interface SettingsVariable {
  name: string;
  value: string;
}

/** `[[NAME]]`, spaces tolerated. `{{...}}` is left alone: it belongs to templateEngine. */
const VARIABLE_PATTERN = /\[\[\s*([A-Za-z0-9_.-]+)\s*\]\]/g;
const WHOLE_VARIABLE_PATTERN = /^\[\[\s*([A-Za-z0-9_.-]+)\s*\]\]$/;

const readVariables = (source: StorageSettingsSource): SettingsVariable[] =>
  (Array.isArray(source.variables) ? source.variables : []).filter(
    (v): v is StorageSettingsVariable => !!v && typeof v.name === "string" && typeof v.value === "string"
  );

const variablesToMap = (variables: SettingsVariable[]): Map<string, string> => {
  const map = new Map<string, string>();
  // Defined twice: the last one wins, like the row the user edited last.
  variables.forEach(({ name, value }) => {
    if (name.trim()) map.set(name.trim(), value);
  });
  return map;
};

/**
 * A value is injected as JSON when it parses as such — `4000`, `true`, `["a"]` —
 * so a placeholder standing for a whole typed field keeps its type (the schema
 * replaces a string in a number field by its default). Quote it to force a
 * string: `"12"` yields the text 12.
 */
const parseVariableValue = (value: string): any => {
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch (err) {
    return value;
  }
};

/** Applies `transform` to every string of a settings tree (keys are left alone). */
const mapSettingsStrings = (value: any, transform: (str: string) => any): any => {
  if (typeof value === "string") return transform(value);
  if (Array.isArray(value)) return value.map((item) => mapSettingsStrings(item, transform));
  if (value && typeof value === "object") {
    const out: any = {};
    Object.keys(value).forEach((key) => {
      out[key] = mapSettingsStrings(value[key], transform);
    });
    return out;
  }
  return value;
};

export interface SettingsSubstitutionResult {
  settings: Settings;
  /** Placeholders found in the template with no value defined (left as-is). */
  missing: string[];
}

/** Replace every `[[NAME]]` of a template by its value. */
export const substituteSettingsVariables = (data: Settings, variables: SettingsVariable[]): SettingsSubstitutionResult => {
  const map = variablesToMap(variables);
  const missing = new Set<string>();
  const settings = mapSettingsStrings(data, (str) => {
    const whole = str.match(WHOLE_VARIABLE_PATTERN);
    if (whole) {
      const value = map.get(whole[1]);
      if (value === undefined) {
        missing.add(whole[1]);
        return str;
      }
      return parseVariableValue(value);
    }
    // Placeholder inside a longer string: always concatenated as text.
    return str.replace(VARIABLE_PATTERN, (match, name) => {
      const value = map.get(name);
      if (value === undefined) {
        missing.add(name);
        return match;
      }
      const parsed = parseVariableValue(value);
      return typeof parsed === "object" && parsed !== null ? JSON.stringify(parsed) : String(parsed);
    });
  });
  return { settings, missing: [...missing] };
};

/** Cheap check: is there anything to substitute at all? */
export const hasSettingsPlaceholder = (data: Settings): boolean => {
  let found = false;
  mapSettingsStrings(data, (str) => {
    if (!found) {
      VARIABLE_PATTERN.lastIndex = 0;
      found = VARIABLE_PATTERN.test(str);
    }
    return str;
  });
  return found;
};

/** How many times each `[[NAME]]` appears in a template (drives the popup counters). */
export const countSettingsVariableOccurrences = (data: Settings): Record<string, number> => {
  const counts: Record<string, number> = {};
  mapSettingsStrings(data, (str) => {
    str.replace(VARIABLE_PATTERN, (match, name) => {
      counts[name] = (counts[name] ?? 0) + 1;
      return match;
    });
    return str;
  });
  return counts;
};

/** Where a config takes its content from (default: local). */
export const getSettingsConfigSource = (key: string): SettingsConfigSource => {
  const source = getSettingsSources()[key];
  if (!source) return { mode: "local", variables: [], variablesMode: "local" };
  const variables = readVariables(source);
  const remoteVariables = typeof source.variablesRemote?.url === "string" ? source.variablesRemote : undefined;
  const variablesPart = {
    variables,
    variablesMode: (remoteVariables ? "remote" : "local") as SettingsConfigMode,
    variablesUrl: remoteVariables?.url,
    variablesFetchedAt: remoteVariables?.fetchedAt,
  };
  if (source.mode === "remote" && typeof source.url === "string") {
    return { mode: "remote", url: source.url, fetchedAt: source.fetchedAt, ...variablesPart };
  }
  return { mode: "local", ...variablesPart };
};

/** Declare (or drop, with `undefined`) the URL the variables are fetched from. */
export const setSettingsConfigVariablesUrl = (key: string, url: string | undefined): void => {
  const sources = getSettingsSources();
  const source = sources[key] ?? { mode: "local" as const };
  if (url) source.variablesRemote = { url };
  else delete source.variablesRemote;
  sources[key] = source;
  setSettingsSources(sources);
  notifyRemoteStatus();
};

/** Declare (or drop, with `undefined`) the remote source of a config. */
export const setSettingsConfigSource = (key: string, source: StorageSettingsSource | undefined): void => {
  const sources = getSettingsSources();
  const previous = sources[key];
  if (source) {
    // Where the variables come from is independent from where the config comes
    // from: keep it unless the caller sets it. Without this, the config load —
    // which rewrites this entry on every launch — would silently drop it.
    sources[key] = {
      ...(previous?.variables ? { variables: previous.variables } : {}),
      ...(previous?.variablesRemote ? { variablesRemote: previous.variablesRemote } : {}),
      ...source,
    };
  } else if (!(key in sources)) return;
  else delete sources[key];
  setSettingsSources(sources);
  notifyRemoteStatus();
};

/** Values feeding the `[[NAME]]` placeholders of a remote config. */
export const getSettingsConfigVariables = (key: string): SettingsVariable[] => getSettingsConfigSource(key).variables ?? [];

/** Persist the variables of a remote config; the app picks them up immediately when it is the active one. */
export const setSettingsConfigVariables = (key: string, variables: SettingsVariable[]): void => {
  // Remote variables are a cache of their URL: editing them here would be lost
  // at the next fetch (the popup shows them read-only for that reason).
  if (getSettingsConfigSource(key).variablesMode === "remote") {
    throw new Error("Les variables de cette configuration proviennent d'une URL et ne peuvent pas être modifiées ici");
  }
  const sources = getSettingsSources();
  const source = sources[key];
  // A local config gets an entry of its own: variables are the only side data
  // it carries (`readConfigData` keeps holding the template itself).
  if (!source && !variables.length) return;
  sources[key] = { ...(source ?? { mode: "local" as const }), variables };
  setSettingsSources(sources);
  applyConfigIfActive(key);
  notifyRemoteStatus();
};

/** True while the config holds settings (a remote one: a cache of a past fetch). */
export const hasSettingsConfigData = (key: string): boolean => Object.keys(readConfigData(key)).length > 0;

/**
 * Fetch and validate a settings URL without applying it — what the popup runs
 * when the URL is defined. Rejects on an unreachable URL or an invalid file;
 * resolves with the settings and the schema warnings (unknown/coerced fields).
 */
export interface RemoteSettingsCheck {
  /** The template exactly as published (placeholders intact) — what gets cached. */
  template: Settings;
  warnings: string[];
  /** Placeholders of the template that the given variables do not cover. */
  missing: string[];
}

export const checkRemoteSettingsUrl = async (url: string, variables: SettingsVariable[] = []): Promise<RemoteSettingsCheck> => {
  const { raw, versionWarning } = await fetchSettingsSource(url);
  const template = normalizeSettings(raw) as Settings;
  // Validate the substituted copy, not the template: a `[[NAME]]` standing for
  // a number would be reported as a type error before it is ever replaced.
  const { settings, missing } = substituteSettingsVariables(template, variables);
  const { warnings } = sanitizeSettings(settings, settingsSchema);
  // Cross-field checks the schema cannot do: a remote config is never refused,
  // so an inconsistency that would kill a publication is reported here.
  const emissionWarnings = validateEmissionSettings(settings).map(({ path, message }) => `${path}: ${message}`);
  const compositionWarnings = validateCompositionSettings(settings).map(({ path, message }) => `${path}: ${message}`);
  return {
    template,
    warnings: [...(versionWarning ? [versionWarning] : []), ...warnings, ...emissionWarnings, ...compositionWarnings],
    missing,
  };
};

// ---- Remote load status (in-memory: a boot/reload state, not persisted) ----

export interface RemoteSettingsStatus {
  loading: boolean;
  /** Message of the last failed load — cleared by a successful one. */
  error?: string;
  /** Schema warnings of the last successful load. */
  warnings?: string[];
  /** Undefined variables of the last successful load. */
  missingVariables?: string[];
  /** Timestamp (ms) of the last successful load in this session. */
  loadedAt?: number;
}

const remoteStatuses = new Map<string, RemoteSettingsStatus>();
const remoteStatusListeners = new Set<() => void>();

/** Config and variables are fetched separately: one status per (config, kind). */
const statusKey = (key: string, kind: "config" | "variables") => `${kind}:${key}`;

const notifyRemoteStatus = () => remoteStatusListeners.forEach((cb) => cb());

const setRemoteStatus = (key: string, status: RemoteSettingsStatus, kind: "config" | "variables" = "config") => {
  remoteStatuses.set(statusKey(key, kind), status);
  notifyRemoteStatus();
};

export const getRemoteSettingsStatus = (key: string): RemoteSettingsStatus | undefined => remoteStatuses.get(statusKey(key, "config"));

/** Load status of the variables file (remote variables only). */
export const getRemoteVariablesStatus = (key: string): RemoteSettingsStatus | undefined => remoteStatuses.get(statusKey(key, "variables"));

export const subscribeRemoteSettingsStatus = (cb: () => void): (() => void) => {
  remoteStatusListeners.add(cb);
  return () => {
    remoteStatusListeners.delete(cb);
  };
};

export interface RemoteSettingsLoadResult {
  ok: boolean;
  error?: string;
  warnings?: string[];
  /** Placeholders left unresolved: the config is loaded but incomplete. */
  missing?: string[];
}

/**
 * Fetch a remote config and cache it; when it is the active one, the app picks
 * the new content up immediately. Never throws: the error is both returned and
 * kept in the config status.
 */
export const loadRemoteSettingsConfig = async (key: string): Promise<RemoteSettingsLoadResult> => {
  const source = getSettingsConfigSource(key);
  if (source.mode !== "remote" || !source.url) return { ok: true };

  setRemoteStatus(key, { ...getRemoteSettingsStatus(key), loading: true });
  try {
    const { template, warnings, missing } = await checkRemoteSettingsUrl(source.url, source.variables);
    writeConfigData(key, template); // cache: the template the next boot starts on
    setSettingsConfigSource(key, {
      mode: "remote",
      url: source.url,
      fetchedAt: Date.now(),
      variables: source.variables,
    });
    applyConfigIfActive(key);
    setRemoteStatus(key, { loading: false, warnings, missingVariables: missing, loadedAt: Date.now() });
    if (warnings.length) console.warn(`[settings] Remote config "${key}" loaded with warnings:`, warnings);
    if (missing.length) console.warn(`[settings] Remote config "${key}" has undefined variables:`, missing);
    return { ok: true, warnings, missing };
  } catch (err) {
    const error = (err as Error)?.message ?? String(err);
    setRemoteStatus(key, { ...getRemoteSettingsStatus(key), loading: false, error });
    console.error(`[settings] Failed to load remote config "${key}" (${source.url}):`, err);
    return { ok: false, error };
  }
};

/**
 * Read a variables file: a flat `{ "NAME": "value" }` object, the very shape
 * the popup exports. A `null` value marks a variable left to define, so it is
 * dropped rather than turned into the text "null".
 */
export const parseRemoteVariables = (raw: any): SettingsVariable[] => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error('Le fichier de variables doit être un objet { "NOM": "valeur" }');
  }
  return Object.entries(raw)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([name, value]) => ({
      // A typed value (`true`, `4000`) is kept as the text that yields it back.
      name,
      value: typeof value === "string" ? value : JSON.stringify(value),
    }));
};

/** Fetch and validate a variables URL without applying it (what the popup checks). */
export const checkRemoteVariablesUrl = async (url: string): Promise<SettingsVariable[]> => {
  // Same fetcher as the config: its errors already name CORS, HTTP status and
  // non-JSON bodies. It strips `_schemaVersion`, which a variables file has no
  // reason to carry.
  const { raw } = await fetchSettingsSource(url);
  return parseRemoteVariables(raw);
};

/**
 * Fetch the variables of a config and cache them; when it is the active config,
 * the app picks them up immediately. Never throws.
 */
export const loadRemoteSettingsVariables = async (key: string): Promise<RemoteSettingsLoadResult> => {
  const source = getSettingsConfigSource(key);
  if (source.variablesMode !== "remote" || !source.variablesUrl) return { ok: true };

  setRemoteStatus(key, { ...getRemoteVariablesStatus(key), loading: true }, "variables");
  try {
    const variables = await checkRemoteVariablesUrl(source.variablesUrl);
    const sources = getSettingsSources();
    const stored = sources[key] ?? { mode: "local" as const };
    sources[key] = { ...stored, variables, variablesRemote: { url: source.variablesUrl, fetchedAt: Date.now() } };
    setSettingsSources(sources);
    applyConfigIfActive(key);
    setRemoteStatus(key, { loading: false, loadedAt: Date.now() }, "variables");
    return { ok: true };
  } catch (err) {
    const error = (err as Error)?.message ?? String(err);
    setRemoteStatus(key, { ...getRemoteVariablesStatus(key), loading: false, error }, "variables");
    console.error(`[settings] Failed to load variables of "${key}" (${source.variablesUrl}):`, err);
    return { ok: false, error };
  }
};

export interface SettingsInitResult {
  /** Key of the active config. */
  key: string;
  mode: SettingsConfigMode;
  url?: string;
  /** Remote load error — the app then runs on the cached content, if any. */
  error?: string;
  /** What failed: reaching/parsing the URL, or resolving the config variables. */
  errorKind?: "fetch" | "variables";
  /** Whether settings are available at all despite the error. */
  hasCache: boolean;
}

/**
 * Boot-time settings resolution, awaited before the app renders: a local config
 * resolves synchronously, a remote one is fetched first (its cache stays in
 * place as the fallback when the fetch fails).
 */
export const initSettings = async (): Promise<SettingsInitResult> => {
  ensureLayers();
  const key = resolveActiveSettingsKey();
  // Variables first: the config load validates the substituted copy, so it
  // needs the values that are about to replace the placeholders.
  const variablesResult = await loadRemoteSettingsVariables(key);
  const source = getSettingsConfigSource(key);
  const variablesError = variablesResult.error ? `Variables distantes inaccessibles :\n${variablesResult.error}` : undefined;
  // A config whose placeholders are not all resolved is reported too: the app
  // would otherwise start on values that read like `[[CAM_IP]]`, unannounced.
  const describeMissing = (missing?: string[]) =>
    missing?.length
      ? `Variables non définies : ${missing.join(
          ", "
        )}.\nLes emplacements correspondants restent tels quels dans la configuration — définissez-les dans les paramètres.`
      : undefined;

  if (source.mode !== "remote") {
    const missingError = describeMissing(resolveConfigData(key).missing);
    const error = variablesError ?? missingError;
    return {
      key,
      mode: "local",
      url: variablesError ? source.variablesUrl : undefined,
      error,
      errorKind: error ? (variablesError ? "fetch" : "variables") : undefined,
      hasCache: true,
    };
  }
  const result = await loadRemoteSettingsConfig(key);
  const missingError = describeMissing(result.missing);
  const error = result.error ?? variablesError ?? missingError;
  return {
    key,
    mode: "remote",
    url: result.error ? source.url : variablesError ? source.variablesUrl : source.url,
    error,
    errorKind: error ? (result.error || variablesError ? "fetch" : "variables") : undefined,
    hasCache: hasSettingsConfigData(key),
  };
};

export const encodeBase64Url = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/** Decodes base64url (or plain base64) to text. Returns undefined if invalid. */
export const decodeBase64Url = (str: string): string | undefined => {
  try {
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)));
  } catch (err) {
    return undefined;
  }
};

/**
 * `?settings=` payload: raw JSON or base64url-encoded JSON. base64url is the
 * preferred form — opaque, so `"`/`&`/`#` never break the query string.
 */
export const parseSettingsPayload = (payload: string): Partial<Settings> | undefined => {
  const json = parseSettingsObject(payload);
  if (json) return json;
  const decoded = decodeBase64Url(payload);
  if (decoded !== undefined) return parseSettingsObject(decoded);
  return undefined;
};

let lastUrlPayload: string | null | undefined; // undefined = never synced

export const syncSettingsUrlOverlay = (href: string): void => {
  const payload = getGlobalQuery(href, NavigateGlobalQuery.SETTINGS) ?? null;
  if (payload === lastUrlPayload) return; // one parse shared by every useSettings instance
  lastUrlPayload = payload;
  let overlay: Partial<Settings> = {};
  if (payload) {
    const parsed = parseSettingsPayload(payload);
    if (parsed) overlay = parsed;
    else console.warn("[settings] Invalid ?settings payload (expected JSON or base64url JSON) — ignored");
  }
  ensureLayers().url = overlay;
  notify();
};
